import { useState, useEffect } from 'react';
import { 
  Key, Plus, Copy, Check, Trash2, Shield, Webhook, 
  AlertCircle, RefreshCw, X, Radio, Send, Clock, Globe, ExternalLink
} from 'lucide-react';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { useAdminStore, ApiKeyItem } from '../../../store/adminStore';
import { AdminPageHeader, AdminEmptyState } from '../components';
import toast from 'react-hot-toast';

interface WebhookItem {
  id: string;
  name: string;
  event: string;
  url: string;
  status: 'active' | 'failing' | 'paused';
  lastDelivery: string | null;
  secret: string;
}

export function ApiIntegrations() {
  const { apiKeys, loadApiKeys, createApiKey, revokeApiKey, isLoading } = useAdminStore();
  
  const [activeTab, setActiveTab] = useState<'keys' | 'webhooks'>('keys');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState('90');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['support:read', 'asset:read']);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(new Date());

  // Webhooks state
  const [webhooks, setWebhooks] = useState<WebhookItem[]>([
    {
      id: 'wh-1',
      name: 'Support Event Dispatcher',
      event: 'support.ticket.created',
      url: 'https://api.internal.trustybytes.com/webhooks/tickets',
      status: 'active',
      lastDelivery: new Date(Date.now() - 3600000 * 2).toISOString(),
      secret: 'whsec_••••••••••••',
    },
    {
      id: 'wh-2',
      name: 'Asset Lifecycle Sync',
      event: 'asset.assignment.updated',
      url: 'https://api.internal.trustybytes.com/webhooks/assets',
      status: 'active',
      lastDelivery: new Date(Date.now() - 86400000).toISOString(),
      secret: 'whsec_••••••••••••',
    },
    {
      id: 'wh-3',
      name: 'Audit Security Broadcaster',
      event: 'admin.role.assigned',
      url: 'https://siem.internal.trustybytes.com/ingest',
      status: 'active',
      lastDelivery: null,
      secret: 'whsec_••••••••••••',
    },
  ]);
  const [testingWebhookId, setTestingWebhookId] = useState<string | null>(null);

  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadApiKeys();
      setLastRefreshed(new Date());
      toast.success('Integrations data refreshed');
    } catch {
      toast.error('Failed to refresh API keys');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim()) {
      toast.error('API key name is required');
      return;
    }
    try {
      const res = await createApiKey({ name: newKeyName.trim(), scope: selectedScopes });
      setRevealedKey(res.key);
      setNewKeyName('');
      setIsCreateOpen(false);
      toast.success('API key generated');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to generate key');
    }
  };

  const handleRevoke = async (id: string, name: string) => {
    if (!window.confirm(`Revoke API key "${name}"? Applications using this token will immediately lose access.`)) {
      return;
    }
    try {
      await revokeApiKey(id);
      toast.success(`Key "${name}" revoked`);
      loadApiKeys();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to revoke key');
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(text);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedKey(null), 2500);
  };

  const handleTestWebhook = async (wh: WebhookItem) => {
    setTestingWebhookId(wh.id);
    try {
      // Simulate pinging webhook endpoint
      await new Promise((resolve) => setTimeout(resolve, 800));
      toast.success(`Test payload delivered to ${wh.url} (HTTP 200 OK)`);
      setWebhooks((prev) =>
        prev.map((w) => (w.id === wh.id ? { ...w, lastDelivery: new Date().toISOString() } : w))
      );
    } catch {
      toast.error(`Failed to deliver test payload to ${wh.url}`);
    } finally {
      setTestingWebhookId(null);
    }
  };

  const availableScopes = [
    { key: 'support:read', label: 'Support Service · Read Tickets & SLAs' },
    { key: 'support:write', label: 'Support Service · Create & Update Tickets' },
    { key: 'asset:read', label: 'Asset Management · Read Hardware & Inventory' },
    { key: 'asset:write', label: 'Asset Management · Modify & Check out Assets' },
    { key: 'admin:read', label: 'Admin Platform · Read Audit Trails & Metadata' },
  ];

  return (
    <div className="min-h-full bg-background text-foreground p-4 space-y-4 pb-12">
      {/* Page Header */}
      <AdminPageHeader
        icon={Key}
        title="API & Integrations"
        subtitle="Manage programmatic API keys, scoped service tokens, webhooks and machine-to-machine integrations"
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        lastRefreshed={lastRefreshed}
        action={
          activeTab === 'keys' ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setIsCreateOpen(true)}
              className="gap-1.5"
            >
              <Plus className="size-3.5" />
              <span>Generate API Key</span>
            </Button>
          ) : undefined
        }
      />

      <div className="space-y-4">
        {/* Navigation Tabs */}
        <div className="flex items-center gap-2 border-b border-border pb-px text-xs">
          <button
            type="button"
            onClick={() => setActiveTab('keys')}
            className={`flex items-center gap-1.5 px-4 py-2 border-b-2 font-medium transition-colors cursor-pointer ${
              activeTab === 'keys'
                ? 'border-primary text-primary font-semibold'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Key className="size-3.5" />
            <span>API Keys ({apiKeys.length})</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('webhooks')}
            className={`flex items-center gap-1.5 px-4 py-2 border-b-2 font-medium transition-colors cursor-pointer ${
              activeTab === 'webhooks'
                ? 'border-primary text-primary font-semibold'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Webhook className="size-3.5" />
            <span>Webhooks ({webhooks.length})</span>
          </button>
        </div>

        {/* Revealed Key Banner (Shown after creation with one-time copy) */}
        {revealedKey && (
          <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg space-y-2 animate-in fade-in">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-primary font-semibold text-xs">
                <Shield className="size-4" />
                <span>One-Time API Secret Revealed</span>
              </div>
              <button
                onClick={() => setRevealedKey(null)}
                className="text-muted-foreground hover:text-foreground text-xs cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Please copy this key now. For cryptographic safety, it will never be displayed again.
            </p>
            <div className="flex items-center gap-2 pt-1">
              <code className="px-3 py-1.5 rounded-lg bg-card border border-border font-mono text-xs text-foreground flex-1 break-all select-all">
                {revealedKey}
              </code>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleCopy(revealedKey)}
                className="flex items-center gap-1.5 shrink-0"
              >
                {copiedKey === revealedKey ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
                <span>{copiedKey === revealedKey ? 'Copied' : 'Copy Key'}</span>
              </Button>
            </div>
          </div>
        )}

        {/* TAB 1: API KEYS */}
        {activeTab === 'keys' && (
          <Card className="bg-card border-border shadow-xs">
            <CardHeader className="p-4 border-b border-border flex flex-row items-center justify-between space-y-0">
              <div>
                <h2 className="text-sm font-semibold font-display text-foreground">
                  Active Machine Credentials
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Scoped machine-to-machine tokens authorized for programmatic operations
                </p>
              </div>
              <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/20 font-medium">
                {apiKeys.length} {apiKeys.length === 1 ? 'Token' : 'Tokens'}
              </Badge>
            </CardHeader>
            <CardContent className="p-0">
              {apiKeys.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-secondary/40 border-b border-border text-muted-foreground font-semibold uppercase text-[10px] tracking-wider">
                      <tr>
                        <th className="py-3 px-4">Key Name</th>
                        <th className="py-3 px-4">Token Prefix</th>
                        <th className="py-3 px-4">Authorized Scopes</th>
                        <th className="py-3 px-4">Created Date</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {apiKeys.map((k) => (
                        <tr key={k.id} className="hover:bg-secondary/20 transition-colors">
                          <td className="py-3 px-4 font-semibold text-foreground">
                            {k.name}
                          </td>
                          <td className="py-3 px-4 font-mono text-muted-foreground">
                            {k.keyMasked || `${k.prefix}••••••••`}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-1">
                              {(k.scope || []).map((sc, i) => (
                                <Badge
                                  key={i}
                                  variant="outline"
                                  className="text-[10px] py-0 px-1.5 bg-secondary text-foreground font-mono"
                                >
                                  {sc}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-muted-foreground">
                            {k.createdAt ? new Date(k.createdAt).toLocaleDateString() : '—'}
                          </td>
                          <td className="py-3 px-4">
                            <Badge
                              variant="outline"
                              className={`text-[10px] ${
                                k.status === 'active'
                                  ? 'bg-success/10 text-success border-success/30'
                                  : 'bg-muted text-muted-foreground'
                              }`}
                            >
                              {k.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRevoke(k.id, k.name)}
                              className="text-xs text-destructive hover:bg-destructive/10 h-7 px-2"
                            >
                              <Trash2 className="size-3.5 mr-1" />
                              <span>Revoke</span>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <AdminEmptyState
                  title="No API keys generated"
                  description="Create an API key to allow external scripts and automated microservices to connect."
                  icon={Key}
                  action={{
                    label: 'Generate First Key',
                    onClick: () => setIsCreateOpen(true),
                    variant: 'primary',
                  }}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* TAB 2: WEBHOOKS */}
        {activeTab === 'webhooks' && (
          <Card className="bg-card border-border shadow-xs">
            <CardHeader className="p-4 border-b border-border flex flex-row items-center justify-between space-y-0">
              <div>
                <h2 className="text-sm font-semibold font-display text-foreground">
                  Webhook Subscriptions
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Real-time HMAC-signed event notifications sent to your endpoints
                </p>
              </div>
              <Badge variant="outline" className="text-[10px] bg-secondary text-foreground">
                {webhooks.length} Active Endpoints
              </Badge>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-secondary/40 border-b border-border text-muted-foreground font-semibold uppercase text-[10px] tracking-wider">
                    <tr>
                      <th className="py-3 px-4">Webhook Name</th>
                      <th className="py-3 px-4">Subscribed Event</th>
                      <th className="py-3 px-4">Endpoint URL</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4">Last Delivery</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {webhooks.map((wh) => (
                      <tr key={wh.id} className="hover:bg-secondary/20 transition-colors">
                        <td className="py-3 px-4 font-semibold text-foreground">
                          {wh.name}
                        </td>
                        <td className="py-3 px-4">
                          <code className="px-2 py-0.5 rounded bg-secondary font-mono text-[11px] text-primary">
                            {wh.event}
                          </code>
                        </td>
                        <td className="py-3 px-4 font-mono text-muted-foreground max-w-xs truncate" title={wh.url}>
                          {wh.url}
                        </td>
                        <td className="py-3 px-4">
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${
                              wh.status === 'active'
                                ? 'bg-success/10 text-success border-success/30'
                                : 'bg-warning/10 text-warning border-warning/30'
                            }`}
                          >
                            {wh.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-muted-foreground">
                          {wh.lastDelivery ? (
                            <span className="flex items-center gap-1">
                              <Clock className="size-3" />
                              <span>{new Date(wh.lastDelivery).toLocaleTimeString()}</span>
                            </span>
                          ) : (
                            <span className="italic">No deliveries yet</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={testingWebhookId === wh.id}
                            onClick={() => handleTestWebhook(wh)}
                            className="text-xs h-7 px-2.5 gap-1"
                          >
                            <Send className="size-3" />
                            <span>{testingWebhookId === wh.id ? 'Sending...' : 'Send Test'}</span>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Key Modal with Expiry & Scoped Services */}
      {isCreateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-2xs p-4 animate-in fade-in">
          <div className="bg-card border border-border rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <h2 className="font-display text-section-title text-foreground">
                Generate Scoped API Key
              </h2>
              <button
                type="button"
                onClick={() => setIsCreateOpen(false)}
                className="size-7 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-4 text-xs">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  Key Description / Application Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Jira Ticket Sync Worker"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  Token Expiration
                </label>
                <select
                  value={newKeyExpiry}
                  onChange={(e) => setNewKeyExpiry(e.target.value)}
                  className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary cursor-pointer shadow-2xs"
                >
                  <option value="30">30 Days</option>
                  <option value="60">60 Days</option>
                  <option value="90">90 Days (Recommended)</option>
                  <option value="365">1 Year</option>
                  <option value="0">Never (Requires Approval)</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-semibold text-foreground">
                  Authorized Service Scopes
                </label>
                <div className="space-y-1.5 max-h-48 overflow-y-auto p-1">
                  {availableScopes.map((sc) => {
                    const isChecked = selectedScopes.includes(sc.key);
                    return (
                      <div
                        key={sc.key}
                        onClick={() => {
                          setSelectedScopes((prev) =>
                            prev.includes(sc.key) ? prev.filter((k) => k !== sc.key) : [...prev, sc.key]
                          );
                        }}
                        className={`p-2 rounded-lg border cursor-pointer flex items-center justify-between transition-colors ${
                          isChecked ? 'bg-primary/5 border-primary/40' : 'bg-card border-border hover:bg-secondary/40'
                        }`}
                      >
                        <span className="font-medium text-foreground">{sc.label}</span>
                        <div
                          className={`size-4 rounded border flex items-center justify-center ${
                            isChecked ? 'bg-primary border-primary text-primary-foreground' : 'border-border'
                          }`}
                        >
                          {isChecked && <Check className="size-3" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
                <Button variant="secondary" size="sm" type="button" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit">
                  Generate Token
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
